import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, ArrowRight, Check, AlertTriangle, TrendingUp, TrendingDown, Zap, Wind, Sun, Clock, Activity, BarChart3, Gauge, Info, ChevronDown, ArrowLeftRight, Bell, X, Save, SlidersHorizontal } from 'lucide-react';
import { useChatGPT, Message } from '../../hooks/useChatGPT';
// --- Helper Functions ---
// Simple markdown renderer for chat messages
const renderChatMarkdown = (content: string): JSX.Element[] => {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let currentList: string[] = [];
  let listKey = 0;
  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(<ul key={`list-${listKey++}`} className="space-y-1 my-2 ml-1" data-id="element-928">
          {currentList.map((item, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-929">
              <span className="text-pink-500 mt-0.5 font-bold text-[10px] flex-shrink-0" data-id="element-930">
                →
              </span>
              <span className="flex-1" dangerouslySetInnerHTML={{
            __html: formatInlineMarkdown(item)
          }} data-id="element-931" />
            </li>)}
        </ul>);
      currentList = [];
    }
  };
  const formatInlineMarkdown = (text: string): string => {
    // Bold: **text**
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
    // Italic: *text*
    text = text.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em class="italic">$1</em>');
    return text;
  };
  lines.forEach((line, idx) => {
    if (line.startsWith('###')) {
      flushList();
      const text = line.replace(/^###\s*/, '');
      elements.push(<h4 key={`h4-${idx}`} className="text-xs font-bold text-slate-800 mt-2 mb-1" dangerouslySetInnerHTML={{
        __html: formatInlineMarkdown(text)
      }} data-id="element-932" />);
    } else if (line.startsWith('##')) {
      flushList();
      const text = line.replace(/^##\s*/, '');
      elements.push(<h3 key={`h3-${idx}`} className="text-sm font-bold text-slate-900 mt-3 mb-1" dangerouslySetInnerHTML={{
        __html: formatInlineMarkdown(text)
      }} data-id="element-933" />);
    } else if (line.match(/^[\-•→]\s+/)) {
      const text = line.replace(/^[\-•→]\s+/, '');
      currentList.push(text);
    } else if (line.trim() === '') {
      flushList();
      elements.push(<div key={`space-${idx}`} className="h-1" data-id="element-934" />);
    } else {
      flushList();
      elements.push(<p key={`p-${idx}`} className="leading-relaxed" dangerouslySetInnerHTML={{
        __html: formatInlineMarkdown(line)
      }} data-id="element-935" />);
    }
  });
  flushList();
  return elements;
};
// Strip markdown for plain text
const stripMarkdown = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/^#+\s+/gm, '').replace(/^[\-•→]\s+/gm, '').trim();
};
// --- Types ---
type DecisionHorizon = 'Intraday' | 'Day-ahead' | 'Short-term' | 'Structure';
type TradingDriver = 'Price dislocations' | 'Weather revisions' | 'System stress' | 'Relative value' | 'Volatility shifts';
type ReassessmentTrigger = 'Large forecast revisions' | 'Model disagreement' | 'Operational events' | 'Auction divergence' | 'Unexplained price moves';
type TradingStyle = 'Outright price' | 'Intraday shape' | 'Regional spreads' | 'Time spreads' | 'Optionality';
type DetailLevel = 'Executive' | 'Tactical' | 'Analytical';
type Region = 'CWE' | 'Germany' | 'France' | 'UK' | 'Spain' | 'Nordics';
interface BriefingPreferences {
  horizon: DecisionHorizon;
  drivers: TradingDriver[];
  reassessmentTriggers: ReassessmentTrigger[];
  tradingStyle: TradingStyle;
  detailLevel: DetailLevel;
  regions: Region[];
}
const DEFAULT_PREFERENCES: BriefingPreferences = {
  horizon: 'Day-ahead',
  drivers: ['Price dislocations', 'Weather revisions'],
  reassessmentTriggers: ['Large forecast revisions'],
  tradingStyle: 'Outright price',
  detailLevel: 'Tactical',
  regions: ['Germany'] // Keep as array for compatibility, but only one region
};
// --- Constants ---
const HORIZON_OPTIONS: DecisionHorizon[] = ['Intraday', 'Day-ahead', 'Short-term', 'Structure'];
const DRIVER_OPTIONS: TradingDriver[] = ['Price dislocations', 'Weather revisions', 'System stress', 'Relative value', 'Volatility shifts'];
const TRIGGER_OPTIONS: ReassessmentTrigger[] = ['Large forecast revisions', 'Model disagreement', 'Operational events', 'Auction divergence', 'Unexplained price moves'];
const STYLE_OPTIONS: TradingStyle[] = ['Outright price', 'Intraday shape', 'Regional spreads', 'Time spreads', 'Optionality'];
const DETAIL_OPTIONS: {
  value: DetailLevel;
  description: string;
}[] = [{
  value: 'Executive',
  description: 'Only what changed and why it matters'
}, {
  value: 'Tactical',
  description: 'Key drivers and deltas with context'
}, {
  value: 'Analytical',
  description: 'Comprehensive data and model details'
}];
const REGION_OPTIONS: Region[] = ['CWE', 'Germany', 'France', 'UK', 'Spain', 'Nordics'];
// --- Dynamic Data Generation ---
// Region-specific market data
const REGION_DATA: Record<Region, {
  currency: string;
  timezone: string;
  market: string;
  basePrice: number;
  priceVolatility: number;
  windCapacity: number;
  solarCapacity: number;
  nuclearCapacity: number;
  gasCapacity: number;
  mainAssets: {
    name: string;
    type: string;
    capacity: number;
  }[];
  interconnectors: {
    name: string;
    partner: string;
    capacity: number;
  }[];
  keyDrivers: string[];
}> = {
  Germany: {
    currency: '€',
    timezone: 'CET',
    market: 'EPEX Spot DE',
    basePrice: 84.5,
    priceVolatility: 12.3,
    windCapacity: 67.4,
    solarCapacity: 81.2,
    nuclearCapacity: 0,
    gasCapacity: 31.5,
    mainAssets: [{
      name: 'Offshore Wind North Sea',
      type: 'Wind',
      capacity: 8.4
    }, {
      name: 'Lignite Fleet Lausitz',
      type: 'Coal',
      capacity: 9.2
    }, {
      name: 'Gas Peakers Bavaria',
      type: 'Gas',
      capacity: 4.1
    }],
    interconnectors: [{
      name: 'DE-FR',
      partner: 'France',
      capacity: 4.8
    }, {
      name: 'DE-NL',
      partner: 'Netherlands',
      capacity: 5.0
    }, {
      name: 'DE-DK',
      partner: 'Denmark',
      capacity: 3.5
    }],
    keyDrivers: ['Wind generation', 'Coal-gas switching', 'Cross-border flows']
  },
  France: {
    currency: '€',
    timezone: 'CET',
    market: 'EPEX Spot FR',
    basePrice: 72.3,
    priceVolatility: 18.7,
    windCapacity: 21.1,
    solarCapacity: 18.9,
    nuclearCapacity: 61.4,
    gasCapacity: 12.3,
    mainAssets: [{
      name: 'Cattenom Nuclear',
      type: 'Nuclear',
      capacity: 5.2
    }, {
      name: 'Gravelines Nuclear',
      type: 'Nuclear',
      capacity: 5.5
    }, {
      name: 'Flamanville Nuclear',
      type: 'Nuclear',
      capacity: 2.6
    }],
    interconnectors: [{
      name: 'FR-GB',
      partner: 'UK',
      capacity: 3.0
    }, {
      name: 'FR-ES',
      partner: 'Spain',
      capacity: 2.8
    }, {
      name: 'FR-DE',
      partner: 'Germany',
      capacity: 4.8
    }],
    keyDrivers: ['Nuclear availability', 'Temperature demand', 'Hydro levels']
  },
  UK: {
    currency: '£',
    timezone: 'GMT',
    market: 'N2EX / EPEX GB',
    basePrice: 91.2,
    priceVolatility: 22.4,
    windCapacity: 29.8,
    solarCapacity: 15.2,
    nuclearCapacity: 5.9,
    gasCapacity: 38.2,
    mainAssets: [{
      name: 'Hornsea Wind Farm',
      type: 'Wind',
      capacity: 2.9
    }, {
      name: 'Hinkley Point B',
      type: 'Nuclear',
      capacity: 1.0
    }, {
      name: 'Drax CCGT',
      type: 'Gas',
      capacity: 2.6
    }],
    interconnectors: [{
      name: 'IFA',
      partner: 'France',
      capacity: 2.0
    }, {
      name: 'BritNed',
      partner: 'Netherlands',
      capacity: 1.0
    }, {
      name: 'NSL',
      partner: 'Norway',
      capacity: 1.4
    }],
    keyDrivers: ['Wind output', 'Gas prices', 'Interconnector flows']
  },
  Spain: {
    currency: '€',
    timezone: 'CET',
    market: 'OMIE',
    basePrice: 68.9,
    priceVolatility: 15.2,
    windCapacity: 30.2,
    solarCapacity: 25.1,
    nuclearCapacity: 7.1,
    gasCapacity: 26.4,
    mainAssets: [{
      name: 'Almaraz Nuclear',
      type: 'Nuclear',
      capacity: 2.1
    }, {
      name: 'Extremadura Solar',
      type: 'Solar',
      capacity: 3.8
    }, {
      name: 'Galicia Wind',
      type: 'Wind',
      capacity: 4.2
    }],
    interconnectors: [{
      name: 'ES-FR',
      partner: 'France',
      capacity: 2.8
    }, {
      name: 'ES-PT',
      partner: 'Portugal',
      capacity: 4.2
    }],
    keyDrivers: ['Solar irradiance', 'Hydro reservoir', 'Iberian demand']
  },
  Nordics: {
    currency: '€',
    timezone: 'CET',
    market: 'Nord Pool',
    basePrice: 42.1,
    priceVolatility: 28.6,
    windCapacity: 35.4,
    solarCapacity: 4.2,
    nuclearCapacity: 11.2,
    gasCapacity: 2.1,
    mainAssets: [{
      name: 'Swedish Hydro Fleet',
      type: 'Hydro',
      capacity: 16.2
    }, {
      name: 'Norwegian Hydro',
      type: 'Hydro',
      capacity: 33.8
    }, {
      name: 'Forsmark Nuclear',
      type: 'Nuclear',
      capacity: 3.3
    }],
    interconnectors: [{
      name: 'NordLink',
      partner: 'Germany',
      capacity: 1.4
    }, {
      name: 'NSL',
      partner: 'UK',
      capacity: 1.4
    }, {
      name: 'Baltic Cable',
      partner: 'Germany',
      capacity: 0.6
    }],
    keyDrivers: ['Hydro levels', 'Wind generation', 'Temperature']
  },
  CWE: {
    currency: '€',
    timezone: 'CET',
    market: 'CWE Coupled',
    basePrice: 78.4,
    priceVolatility: 14.1,
    windCapacity: 120.5,
    solarCapacity: 125.3,
    nuclearCapacity: 61.4,
    gasCapacity: 82.4,
    mainAssets: [{
      name: 'Belgian Nuclear Fleet',
      type: 'Nuclear',
      capacity: 5.9
    }, {
      name: 'Dutch Gas Fleet',
      type: 'Gas',
      capacity: 18.2
    }, {
      name: 'CWE Offshore Wind',
      type: 'Wind',
      capacity: 12.4
    }],
    interconnectors: [{
      name: 'CWE Internal',
      partner: 'Multiple',
      capacity: 25.0
    }, {
      name: 'CWE-Nordic',
      partner: 'Nordics',
      capacity: 4.2
    }],
    keyDrivers: ['Regional coupling', 'Renewable correlation', 'Gas prices']
  }
};
// Horizon-specific configurations
const HORIZON_CONFIG: Record<DecisionHorizon, {
  timeframe: string;
  delivery: string;
  focusAreas: string[];
  updateFrequency: string;
  keyMetrics: string[];
}> = {
  Intraday: {
    timeframe: 'Next 1-4 hours',
    delivery: 'Continuous trading',
    focusAreas: ['Real-time imbalance', 'Quarter-hour products', 'System frequency'],
    updateFrequency: 'Every 15 minutes',
    keyMetrics: ['Imbalance price', 'Quarter-hour spread', 'System state']
  },
  'Day-ahead': {
    timeframe: 'Tomorrow (D+1)',
    delivery: 'Day-ahead auction',
    focusAreas: ['Hourly prices', 'Peak/off-peak spread', 'Forecast accuracy'],
    updateFrequency: 'Hourly until gate closure',
    keyMetrics: ['Baseload price', 'Peak premium', 'Shape value']
  },
  'Short-term': {
    timeframe: 'Next 3-7 days',
    delivery: 'Week-ahead products',
    focusAreas: ['Weather patterns', 'Demand forecast', 'Outage schedule'],
    updateFrequency: 'Twice daily',
    keyMetrics: ['Weekly average', 'Volatility forecast', 'Spread opportunities']
  },
  Structure: {
    timeframe: 'Months to quarters',
    delivery: 'Forward/futures',
    focusAreas: ['Seasonal patterns', 'Capacity outlook', 'Policy changes'],
    updateFrequency: 'Daily summary',
    keyMetrics: ['Cal spread', 'Q-Q spread', 'Dark/spark spread']
  }
};
// Generate dynamic briefing data based on all preferences
function generateBriefingData(prefs: BriefingPreferences) {
  const primaryRegion = prefs.regions[0] || 'Germany';
  const regionData = REGION_DATA[primaryRegion];
  const horizonConfig = HORIZON_CONFIG[prefs.horizon];
  // Seed for consistent but varied data
  const seed = primaryRegion.length + prefs.horizon.length + prefs.drivers.length;
  const variance = (base: number, range: number) => base + Math.sin(seed) * range;
  // Generate price data based on region and trading style
  const generatePriceData = () => {
    const base = regionData.basePrice;
    const vol = regionData.priceVolatility;
    switch (prefs.tradingStyle) {
      case 'Regional spreads':
        const spreadPartner = prefs.regions[1] || (primaryRegion === 'Germany' ? 'France' : 'Germany');
        const partnerData = REGION_DATA[spreadPartner as Region] || REGION_DATA.Germany;
        return {
          main: {
            label: `${primaryRegion} Base`,
            value: base,
            delta: variance(2.1, 3)
          },
          secondary: {
            label: `${primaryRegion.slice(0, 2).toUpperCase()}-${spreadPartner.slice(0, 2).toUpperCase()} Spread`,
            value: base - partnerData.basePrice,
            delta: variance(-1.2, 2),
            highlight: true
          },
          tertiary: prefs.detailLevel === 'Analytical' ? {
            label: 'Spread Percentile',
            value: `${Math.round(45 + variance(0, 30))}th`,
            context: 'vs 90-day range'
          } : null
        };
      case 'Intraday shape':
        return {
          main: {
            label: `${primaryRegion} Peak`,
            value: base * 1.18,
            delta: variance(4.2, 3)
          },
          secondary: {
            label: 'Peak/Off-peak Ratio',
            value: `${(1.18 + variance(0, 0.1)).toFixed(2)}x`,
            delta: 0,
            highlight: true
          },
          tertiary: prefs.detailLevel === 'Analytical' ? {
            label: 'Evening Ramp',
            value: `+${Math.round(12 + variance(0, 5))}%`,
            context: 'HE17-HE20'
          } : null
        };
      case 'Time spreads':
        return {
          main: {
            label: `${prefs.horizon === 'Structure' ? 'Q1-25' : 'Week+1'}`,
            value: base * 1.05,
            delta: variance(1.8, 2)
          },
          secondary: {
            label: `${prefs.horizon === 'Structure' ? 'Q1-Q2' : 'W1-W2'} Spread`,
            value: variance(2.4, 1.5),
            delta: variance(0.3, 0.5),
            highlight: true
          },
          tertiary: prefs.detailLevel === 'Analytical' ? {
            label: 'Contango/Backwardation',
            value: variance(2.4, 1.5) > 0 ? 'Contango' : 'Backwardation',
            context: `${Math.abs(variance(2.4, 1.5)).toFixed(1)} €/MWh`
          } : null
        };
      case 'Optionality':
        return {
          main: {
            label: `${primaryRegion} ATM Vol`,
            value: vol,
            delta: variance(1.2, 2)
          },
          secondary: {
            label: 'Vol Skew (25Δ)',
            value: `${variance(2.1, 1.5).toFixed(1)}%`,
            delta: 0,
            highlight: true
          },
          tertiary: prefs.detailLevel === 'Analytical' ? {
            label: 'Term Structure',
            value: variance(0, 1) > 0 ? 'Upward' : 'Flat',
            context: '1M vs 3M'
          } : null
        };
      default:
        // Outright price
        return {
          main: {
            label: `${primaryRegion} Baseload`,
            value: base,
            delta: variance(3.2, 2.5)
          },
          secondary: prefs.detailLevel !== 'Executive' ? {
            label: 'vs Fundamental',
            value: `${variance(1.5, 2) > 0 ? '+' : ''}${variance(1.5, 2).toFixed(1)}`,
            delta: 0,
            highlight: Math.abs(variance(1.5, 2)) > 2
          } : null,
          tertiary: prefs.detailLevel === 'Analytical' ? {
            label: 'Price Percentile',
            value: `${Math.round(55 + variance(0, 25))}th`,
            context: 'YTD range'
          } : null
        };
    }
  };
  // Generate driver-specific insights
  const generateDriverInsights = () => {
    const insights: Array<{
      driver: string;
      icon: string;
      trend: 'up' | 'down' | 'neutral';
      headline: string;
      delta: string;
      impact: string;
      confidence: string;
      details?: string;
      modelAgreement?: string;
    }> = [];
    if (prefs.drivers.includes('Weather revisions')) {
      const windDelta = variance(-2.4, 1.5);
      const solarDelta = variance(0.8, 0.6);
      insights.push({
        driver: 'Wind Generation',
        icon: 'wind',
        trend: windDelta < 0 ? 'down' : 'up',
        headline: windDelta < 0 ? 'Bearish revision in wind forecast' : 'Bullish wind revision',
        delta: `${windDelta > 0 ? '+' : ''}${windDelta.toFixed(1)} GW`,
        impact: `${Math.abs(windDelta * 3.2).toFixed(1)} €/MWh price impact`,
        confidence: Math.abs(windDelta) > 2 ? 'High agreement' : 'Moderate spread',
        details: prefs.detailLevel !== 'Executive' ? `ECMWF and GFS ${Math.abs(windDelta) > 2 ? 'aligned' : 'diverging'} on ${prefs.horizon === 'Intraday' ? 'next 4h' : 'peak hours'} outlook.` : undefined,
        modelAgreement: prefs.detailLevel === 'Analytical' ? `EPT-2: ${(windDelta - 0.3).toFixed(1)} | GFS: ${(windDelta + 0.2).toFixed(1)} | ECMWF: ${windDelta.toFixed(1)}` : undefined
      });
      if (prefs.detailLevel !== 'Executive' && (primaryRegion === 'Spain' || primaryRegion === 'Germany')) {
        insights.push({
          driver: 'Solar Output',
          icon: 'sun',
          trend: solarDelta > 0 ? 'up' : 'down',
          headline: solarDelta > 0 ? 'Solar forecast upgraded' : 'Cloud cover increasing',
          delta: `${solarDelta > 0 ? '+' : ''}${solarDelta.toFixed(1)} GW`,
          impact: `${Math.abs(solarDelta * 2.1).toFixed(1)} €/MWh mid-day`,
          confidence: 'High confidence',
          details: prefs.detailLevel === 'Analytical' ? `Clear sky index at ${(0.82 + variance(0, 0.1)).toFixed(2)} for ${primaryRegion}.` : undefined
        });
      }
    }
    if (prefs.drivers.includes('System stress')) {
      const marginDelta = variance(-1.8, 1.2);
      insights.push({
        driver: 'System Margin',
        icon: 'gauge',
        trend: marginDelta < 0 ? 'down' : 'up',
        headline: marginDelta < -1 ? 'Tightening system margins' : 'Adequate reserve margin',
        delta: `${marginDelta > 0 ? '+' : ''}${marginDelta.toFixed(1)} GW`,
        impact: marginDelta < -1 ? 'Elevated scarcity risk' : 'Normal operations expected',
        confidence: 'TSO confirmed',
        details: prefs.detailLevel !== 'Executive' ? `Available capacity at ${(regionData.gasCapacity + regionData.nuclearCapacity + marginDelta).toFixed(1)} GW vs ${(regionData.gasCapacity + regionData.nuclearCapacity + 5).toFixed(1)} GW demand forecast.` : undefined
      });
    }
    if (prefs.drivers.includes('Price dislocations')) {
      const dislocation = variance(2.8, 2);
      insights.push({
        driver: 'Price Dislocation',
        icon: 'activity',
        trend: Math.abs(dislocation) > 2 ? dislocation > 0 ? 'up' : 'down' : 'neutral',
        headline: Math.abs(dislocation) > 2 ? `${dislocation > 0 ? 'Premium' : 'Discount'} vs fundamentals` : 'Prices near fair value',
        delta: `${dislocation > 0 ? '+' : ''}${dislocation.toFixed(1)} €/MWh`,
        impact: Math.abs(dislocation) > 3 ? 'Mean reversion opportunity' : 'Limited alpha',
        confidence: `${Math.round(65 + variance(0, 20))}% model confidence`,
        details: prefs.detailLevel !== 'Executive' ? `Current price ${Math.abs(dislocation) > 2 ? 'outside' : 'within'} 1σ band of fundamental estimate.` : undefined
      });
    }
    if (prefs.drivers.includes('Relative value')) {
      const rvScore = variance(0.6, 0.8);
      insights.push({
        driver: 'Relative Value',
        icon: 'arrows',
        trend: rvScore > 0.3 ? 'up' : rvScore < -0.3 ? 'down' : 'neutral',
        headline: rvScore > 0.3 ? 'Cross-market opportunity detected' : 'Spreads near equilibrium',
        delta: `${rvScore > 0 ? '+' : ''}${rvScore.toFixed(2)} z-score`,
        impact: Math.abs(rvScore) > 0.5 ? 'Actionable spread trade' : 'Monitor for entry',
        confidence: `Historical win rate: ${Math.round(55 + Math.abs(rvScore) * 15)}%`,
        details: prefs.detailLevel === 'Analytical' ? `${primaryRegion} vs ${prefs.regions[1] || 'CWE'} spread at ${Math.round(35 + variance(0, 30))}th percentile of 90-day range.` : undefined
      });
    }
    if (prefs.drivers.includes('Volatility shifts')) {
      const volChange = variance(1.8, 2.5);
      insights.push({
        driver: 'Volatility Regime',
        icon: 'chart',
        trend: volChange > 1 ? 'up' : volChange < -1 ? 'down' : 'neutral',
        headline: volChange > 1 ? 'Volatility expanding' : volChange < -1 ? 'Volatility compressing' : 'Stable vol regime',
        delta: `${volChange > 0 ? '+' : ''}${volChange.toFixed(1)}% realized`,
        impact: volChange > 2 ? 'Options premium elevated' : 'Normal premium levels',
        confidence: `Vol percentile: ${Math.round(50 + volChange * 10)}th`,
        details: prefs.detailLevel === 'Analytical' ? `Implied vs realized spread at ${(volChange * 0.8).toFixed(1)}%. ${volChange > 1 ? 'Consider selling vol.' : 'Vol fairly priced.'}` : undefined
      });
    }
    return insights;
  };
  // Generate reassessment triggers
  const generateTriggers = () => {
    const triggers: Array<{
      trigger: string;
      status: 'active' | 'watch' | 'clear';
      description: string;
      threshold: string;
      current: string;
    }> = [];
    if (prefs.reassessmentTriggers.includes('Large forecast revisions')) {
      const revisionSize = Math.abs(variance(2.1, 1.5));
      triggers.push({
        trigger: 'Forecast Revision',
        status: revisionSize > 2 ? 'active' : revisionSize > 1 ? 'watch' : 'clear',
        description: revisionSize > 2 ? 'Significant revision detected' : 'Within normal range',
        threshold: '> 2 GW change',
        current: `${revisionSize.toFixed(1)} GW`
      });
    }
    if (prefs.reassessmentTriggers.includes('Model disagreement')) {
      const disagreement = Math.abs(variance(1.2, 1.8));
      triggers.push({
        trigger: 'Model Spread',
        status: disagreement > 2 ? 'active' : disagreement > 1.2 ? 'watch' : 'clear',
        description: disagreement > 2 ? 'Models diverging significantly' : 'Reasonable consensus',
        threshold: '> 2 GW spread',
        current: `${disagreement.toFixed(1)} GW`
      });
    }
    if (prefs.reassessmentTriggers.includes('Operational events')) {
      const hasEvent = variance(0, 1) > 0.3;
      triggers.push({
        trigger: 'Operational Event',
        status: hasEvent ? 'active' : 'clear',
        description: hasEvent ? 'Unplanned outage reported' : 'No significant events',
        threshold: '> 500 MW unplanned',
        current: hasEvent ? `${(0.8 + variance(0, 0.6)).toFixed(1)} GW offline` : 'Normal'
      });
    }
    if (prefs.reassessmentTriggers.includes('Auction divergence')) {
      const divergence = Math.abs(variance(3.2, 4));
      triggers.push({
        trigger: 'Auction vs Forecast',
        status: divergence > 5 ? 'active' : divergence > 3 ? 'watch' : 'clear',
        description: divergence > 5 ? 'Auction result surprised' : 'Auction as expected',
        threshold: '> 5 €/MWh miss',
        current: `${divergence.toFixed(1)} €/MWh`
      });
    }
    if (prefs.reassessmentTriggers.includes('Unexplained price moves')) {
      const unexplained = Math.abs(variance(2.5, 3));
      triggers.push({
        trigger: 'Unexplained Move',
        status: unexplained > 4 ? 'active' : unexplained > 2.5 ? 'watch' : 'clear',
        description: unexplained > 4 ? 'Price move exceeds fundamentals' : 'Moves explained by data',
        threshold: '> 4 €/MWh residual',
        current: `${unexplained.toFixed(1)} €/MWh`
      });
    }
    return triggers;
  };
  // Generate risk flags based on all preferences
  const generateRiskFlags = () => {
    const risks: Array<{
      severity: 'high' | 'medium' | 'low';
      title: string;
      description: string;
      probability: number;
      impact: string;
    }> = [];
    // Region-specific risks
    if (primaryRegion === 'France' || prefs.regions.includes('France')) {
      if (prefs.drivers.includes('System stress')) {
        risks.push({
          severity: 'high',
          title: 'Nuclear Availability Risk',
          description: `French nuclear fleet at ${(58 + variance(0, 8)).toFixed(0)}% availability. Corrosion checks ongoing at ${Math.round(2 + variance(0, 2))} reactors.`,
          probability: 35,
          impact: '+15-25 €/MWh if further outages'
        });
      }
    }
    if (primaryRegion === 'Germany' || prefs.regions.includes('Germany')) {
      if (prefs.drivers.includes('Weather revisions')) {
        risks.push({
          severity: variance(0, 1) > 0.5 ? 'high' : 'medium',
          title: 'Wind Ramp Risk',
          description: `Residual load approaching thermal stack inflection at ${(62 + variance(0, 5)).toFixed(0)} GW. Small wind drops trigger gas peakers.`,
          probability: Math.round(25 + variance(0, 20)),
          impact: '+€15-20/MWh spike potential'
        });
      }
    }
    if (primaryRegion === 'UK' || prefs.regions.includes('UK')) {
      risks.push({
        severity: 'medium',
        title: 'Interconnector Dependency',
        description: `UK importing ${(2.8 + variance(0, 1)).toFixed(1)} GW via IFA/BritNed. Cable trip would tighten margins significantly.`,
        probability: 15,
        impact: '+£20-30/MWh if flow reversal'
      });
    }
    if (prefs.tradingStyle === 'Optionality' || prefs.drivers.includes('Volatility shifts')) {
      risks.push({
        severity: 'low',
        title: 'Gamma Exposure',
        description: `High gamma zone approaching for ${prefs.horizon} expiries. Delta hedging costs elevated.`,
        probability: 45,
        impact: 'Increased hedging frequency needed'
      });
    }
    // Horizon-specific risks
    if (prefs.horizon === 'Intraday') {
      risks.push({
        severity: 'medium',
        title: 'Liquidity Window',
        description: 'Intraday liquidity drops significantly after 14:00 CET. Plan position adjustments accordingly.',
        probability: 80,
        impact: 'Wider bid-ask spreads'
      });
    }
    return risks.slice(0, prefs.detailLevel === 'Executive' ? 1 : prefs.detailLevel === 'Tactical' ? 2 : 4);
  };
  // Generate operational events
  const generateOperationalEvents = () => {
    const events: Array<{
      asset: string;
      type: string;
      impact: string;
      status: 'unplanned' | 'planned' | 'returning';
      region: string;
    }> = [];
    // Add region-specific events
    regionData.mainAssets.forEach((asset, idx) => {
      if (variance(idx, 1) > 0.5) {
        events.push({
          asset: asset.name,
          type: asset.type === 'Nuclear' ? 'Outage' : asset.type === 'Wind' ? 'Curtailment' : 'Maintenance',
          impact: `-${(asset.capacity * (0.3 + variance(idx, 0.3))).toFixed(1)} GW`,
          status: variance(idx, 1) > 0.7 ? 'unplanned' : 'planned',
          region: primaryRegion
        });
      }
    });
    // Add interconnector events
    if (prefs.detailLevel !== 'Executive') {
      regionData.interconnectors.forEach((ic, idx) => {
        if (variance(idx + 10, 1) > 0.7) {
          events.push({
            asset: ic.name,
            type: 'Interconnector',
            impact: `${Math.round(ic.capacity * 0.4)} MW reduced`,
            status: 'planned',
            region: `${primaryRegion}-${ic.partner}`
          });
        }
      });
    }
    return events.slice(0, prefs.detailLevel === 'Executive' ? 2 : prefs.detailLevel === 'Tactical' ? 4 : 6);
  };
  // Generate executive summary
  const generateExecutiveSummary = () => {
    const driverInsights = generateDriverInsights();
    const mainDriver = driverInsights[0];
    if (!mainDriver) {
      return `${primaryRegion} markets showing stable conditions for ${horizonConfig.timeframe.toLowerCase()}. Monitor ${regionData.keyDrivers[0]} for potential opportunities.`;
    }
    const tradingContext = {
      'Outright price': 'directional price',
      'Intraday shape': 'shape value',
      'Regional spreads': 'spread',
      'Time spreads': 'calendar spread',
      Optionality: 'volatility'
    }[prefs.tradingStyle];
    return `${mainDriver.headline} in ${primaryRegion} (${mainDriver.delta}), creating ${mainDriver.trend === 'neutral' ? 'limited' : 'potential'} ${tradingContext} opportunity for ${horizonConfig.timeframe.toLowerCase()}. ${mainDriver.impact}.`;
  };
  return {
    region: primaryRegion,
    regionData,
    horizonConfig,
    priceData: generatePriceData(),
    driverInsights: generateDriverInsights(),
    triggers: generateTriggers(),
    riskFlags: generateRiskFlags(),
    operationalEvents: generateOperationalEvents(),
    executiveSummary: generateExecutiveSummary()
  };
}
// --- Components ---
const OnboardingModal = ({
  isOpen,
  onComplete,
  initialPrefs
}: {
  isOpen: boolean;
  onComplete: (prefs: BriefingPreferences) => void;
  initialPrefs?: BriefingPreferences;
}) => {
  const [step, setStep] = useState(0);
  const [prefs, setPrefs] = useState<BriefingPreferences>(initialPrefs || DEFAULT_PREFERENCES);
  // Reset step when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      if (initialPrefs) {
        setPrefs(initialPrefs);
      }
    }
  }, [isOpen, initialPrefs]);
  const questions = [{
    id: 'horizon',
    title: 'Primary Decision Horizon',
    question: 'How far ahead do you usually act on information?',
    options: HORIZON_OPTIONS,
    multi: false,
    field: 'horizon'
  }, {
    id: 'drivers',
    title: 'Key Drivers',
    question: 'What typically drives your trading decisions?',
    options: DRIVER_OPTIONS,
    multi: true,
    field: 'drivers'
  }, {
    id: 'triggers',
    title: 'Reassessment Triggers',
    question: 'What usually forces you to reassess a position?',
    options: TRIGGER_OPTIONS,
    multi: true,
    field: 'reassessmentTriggers'
  }, {
    id: 'style',
    title: 'Trading Style',
    question: 'How do you mainly trade value?',
    options: STYLE_OPTIONS,
    multi: false,
    field: 'tradingStyle'
  }, {
    id: 'detail',
    title: 'Information Density',
    question: 'How much detail do you want by default?',
    options: DETAIL_OPTIONS.map(o => o.value),
    multi: false,
    field: 'detailLevel',
    descriptions: DETAIL_OPTIONS.reduce((acc, curr) => ({
      ...acc,
      [curr.value]: curr.description
    }), {})
  }, {
    id: 'regions',
    title: 'Primary Region',
    question: 'Which region do you primarily trade?',
    subtitle: 'Cross-border flows for connected markets will be shown automatically.',
    options: REGION_OPTIONS,
    multi: false,
    field: 'regions',
    singleToArray: true // Flag to handle conversion
  }];
  const handleSelect = (option: string) => {
    const q = questions[step];
    // @ts-ignore
    const currentVal = prefs[q.field];
    if (q.multi) {
      const arr = currentVal as string[];
      let newArr;
      if (arr.includes(option)) {
        newArr = arr.filter(i => i !== option);
      } else {
        if (q.max && arr.length >= q.max) return;
        newArr = [...arr, option];
      }
      setPrefs({
        ...prefs,
        [q.field]: newArr
      });
    } else if (q.singleToArray) {
      // Special handling for regions - store as single-item array
      setPrefs({
        ...prefs,
        [q.field]: [option]
      });
    } else {
      setPrefs({
        ...prefs,
        [q.field]: option
      });
    }
  };
  const currentQ = questions[step];
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm" data-id="element-936">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-937">
        {/* Progress Bar */}
        <div className="h-1 bg-slate-100 w-full" data-id="element-938">
          <motion.div className="h-full bg-pink-500" initial={{
          width: 0
        }} animate={{
          width: `${(step + 1) / questions.length * 100}%`
        }} data-id="element-939" />
        </div>

        <div className="p-8 flex-1 overflow-y-auto" data-id="element-940">
          <span className="text-xs font-bold text-pink-600 uppercase tracking-wider mb-2 block" data-id="element-941">
            Step {step + 1} of {questions.length}
          </span>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-942">
            {currentQ.title}
          </h2>
          <p className="text-slate-600 mb-2" data-id="element-943">{currentQ.question}</p>
          {/* @ts-ignore */}
          {currentQ.subtitle && <p className="text-sm text-slate-500 mb-6 flex items-center gap-2" data-id="element-944">
              <ArrowLeftRight size={14} className="text-blue-500" data-id="element-945" />
              {/* @ts-ignore */}
              {currentQ.subtitle}
            </p>}
          {/* @ts-ignore */}
          {!currentQ.subtitle && <div className="mb-6" data-id="element-946" />}

          <div className="space-y-3" data-id="element-947">
            {currentQ.options.map(opt => {
            // @ts-ignore
            const isSelected = currentQ.multi ? prefs[currentQ.field].includes(opt) : currentQ.singleToArray ? prefs[currentQ.field][0] === opt : prefs[currentQ.field] === opt;
            return <button key={opt} onClick={() => handleSelect(opt)} className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between group ${isSelected ? 'border-pink-500 bg-pink-50 text-pink-900' : 'border-slate-100 hover:border-slate-300 text-slate-700'}`} data-id="element-948">
                  <div data-id="element-949">
                    <span className="font-semibold block" data-id="element-950">{opt}</span>
                    {/* @ts-ignore */}
                    {currentQ.descriptions && currentQ.descriptions[opt] && <span className="text-xs opacity-70 mt-1 block font-normal" data-id="element-951">
                        {/* @ts-ignore */}
                        {currentQ.descriptions[opt]}
                      </span>}
                    {/* Show interconnectors for region options */}
                    {currentQ.id === 'regions' && REGION_DATA[opt as Region] && <span className="text-xs text-slate-500 mt-1 block font-normal" data-id="element-952">
                          Connected:{' '}
                          {REGION_DATA[opt as Region].interconnectors.map(ic => ic.partner).join(', ')}
                        </span>}
                  </div>
                  {isSelected && <Check size={20} className="text-pink-600" data-id="element-953" />}
                </button>;
          })}
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center" data-id="element-954">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${step === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-200'}`} data-id="element-955">
            Back
          </button>

          <button onClick={() => {
          if (step === questions.length - 1) {
            onComplete(prefs);
          } else {
            setStep(step + 1);
          }
        }} className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-slate-800 transition-colors flex items-center gap-2" data-id="element-956">
            {step === questions.length - 1 ? 'Finish' : 'Next'}
            <ArrowRight size={16} data-id="element-957" />
          </button>
        </div>
      </motion.div>
    </div>;
};
const PreferencesPanel = ({
  isOpen,
  onClose,
  onSave,
  initialPrefs
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prefs: BriefingPreferences) => void;
  initialPrefs: BriefingPreferences;
}) => {
  const [prefs, setPrefs] = useState<BriefingPreferences>(initialPrefs);
  // Update local state when initialPrefs changes
  useEffect(() => {
    setPrefs(initialPrefs);
  }, [initialPrefs, isOpen]);
  const handleToggle = <K extends keyof BriefingPreferences,>(field: K, value: any, multi: boolean = false, max?: number) => {
    if (multi) {
      const currentArr = prefs[field] as any[];
      let newArr;
      if (currentArr.includes(value)) {
        newArr = currentArr.filter(item => item !== value);
      } else {
        if (max && currentArr.length >= max) return;
        newArr = [...currentArr, value];
      }
      setPrefs({
        ...prefs,
        [field]: newArr
      });
    } else {
      setPrefs({
        ...prefs,
        [field]: value
      });
    }
  };
  // Special handler for single region selection (stored as array)
  const handleRegionSelect = (region: Region) => {
    setPrefs({
      ...prefs,
      regions: [region]
    });
  };
  return <AnimatePresence data-id="element-958">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[90]" data-id="element-959" />

          {/* Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-[100] flex flex-col" data-id="element-960">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-961">
              <div data-id="element-962">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-963">
                  Briefing Preferences
                </h2>
                <p className="text-sm text-slate-500" data-id="element-964">
                  Customize your daily briefing content and focus
                </p>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-965">
                <X size={24} data-id="element-966" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-967">
              {/* Decision Horizon */}
              <section data-id="element-968">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-969">
                  Decision Horizon
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-970">
                  {HORIZON_OPTIONS.map(opt => <button key={opt} onClick={() => handleToggle('horizon', opt)} className={`p-3 rounded-xl border-2 text-left transition-all ${prefs.horizon === opt ? 'border-pink-500 bg-pink-50 text-pink-900' : 'border-slate-100 hover:border-slate-200 text-slate-600'}`} data-id="element-971">
                      <span className="font-semibold block" data-id="element-972">{opt}</span>
                    </button>)}
                </div>
              </section>

              {/* Trading Style */}
              <section data-id="element-973">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-974">
                  Trading Style
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-975">
                  {STYLE_OPTIONS.map(opt => <button key={opt} onClick={() => handleToggle('tradingStyle', opt)} className={`p-3 rounded-xl border-2 text-left transition-all ${prefs.tradingStyle === opt ? 'border-pink-500 bg-pink-50 text-pink-900' : 'border-slate-100 hover:border-slate-200 text-slate-600'}`} data-id="element-976">
                      <span className="font-semibold text-sm block" data-id="element-977">{opt}</span>
                    </button>)}
                </div>
              </section>

              {/* Information Density */}
              <section data-id="element-978">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-979">
                  Information Density
                </h3>
                <div className="space-y-3" data-id="element-980">
                  {DETAIL_OPTIONS.map(opt => <button key={opt.value} onClick={() => handleToggle('detailLevel', opt.value)} className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between group ${prefs.detailLevel === opt.value ? 'border-pink-500 bg-pink-50 text-pink-900' : 'border-slate-100 hover:border-slate-200 text-slate-600'}`} data-id="element-981">
                      <div data-id="element-982">
                        <span className="font-semibold block" data-id="element-983">{opt.value}</span>
                        <span className="text-xs opacity-70 mt-0.5 block" data-id="element-984">
                          {opt.description}
                        </span>
                      </div>
                      {prefs.detailLevel === opt.value && <Check size={18} className="text-pink-600" data-id="element-985" />}
                    </button>)}
                </div>
              </section>

              {/* Key Drivers */}
              <section data-id="element-986">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-987">
                  Key Drivers
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-988">
                  {DRIVER_OPTIONS.map(opt => {
                const isSelected = prefs.drivers.includes(opt);
                return <button key={opt} onClick={() => handleToggle('drivers', opt, true)} className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${isSelected ? 'border-pink-500 bg-pink-50 text-pink-900' : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'}`} data-id="element-989">
                        {opt}
                      </button>;
              })}
                </div>
              </section>

              {/* Reassessment Triggers */}
              <section data-id="element-990">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-991">
                  Reassessment Triggers
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-992">
                  {TRIGGER_OPTIONS.map(opt => {
                const isSelected = prefs.reassessmentTriggers.includes(opt);
                return <button key={opt} onClick={() => handleToggle('reassessmentTriggers', opt, true)} className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${isSelected ? 'border-pink-500 bg-pink-50 text-pink-900' : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'}`} data-id="element-993">
                        {opt}
                      </button>;
              })}
                </div>
              </section>

              {/* Primary Region - Changed from multi-select to single-select */}
              <section data-id="element-994">
                <div className="flex items-center justify-between mb-3" data-id="element-995">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-996">
                    Primary Region
                  </h3>
                </div>
                <p className="text-xs text-slate-500 mb-3 flex items-center gap-2" data-id="element-997">
                  <ArrowLeftRight size={12} className="text-blue-500" data-id="element-998" />
                  Cross-border flows for connected markets shown automatically
                </p>
                <div className="grid grid-cols-3 gap-3" data-id="element-999">
                  {REGION_OPTIONS.map(opt => {
                const isSelected = prefs.regions[0] === opt;
                const regionData = REGION_DATA[opt];
                return <button key={opt} onClick={() => handleRegionSelect(opt)} className={`p-3 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-pink-500 bg-pink-50 text-pink-900' : 'border-slate-100 hover:border-slate-200 text-slate-600'}`} data-id="element-1000">
                        <span className="font-semibold text-sm block" data-id="element-1001">
                          {opt}
                        </span>
                        {regionData && <span className="text-[10px] text-slate-400 mt-1 block" data-id="element-1002">
                            →{' '}
                            {regionData.interconnectors.slice(0, 2).map(ic => ic.partner).join(', ')}
                          </span>}
                      </button>;
              })}
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3" data-id="element-1003">
              <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-200 transition-colors" data-id="element-1004">
                Cancel
              </button>
              <button onClick={() => onSave(prefs)} className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-slate-800 transition-colors flex items-center gap-2 shadow-lg shadow-slate-900/20" data-id="element-1005">
                <Save size={18} data-id="element-1006" />
                Save Changes
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
// --- Chat Panel Component ---
const ChatPanel = ({
  messages,
  sendMessage,
  isLoading,
  isCollapsed,
  toggleCollapse,
  briefingContext,
  suggestedFollowUps = []
}: {
  messages: Message[];
  sendMessage: (msg: string) => void;
  isLoading: boolean;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  briefingContext: string;
  suggestedFollowUps?: string[];
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [saveToastMessage, setSaveToastMessage] = useState('');
  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages, isLoading]);
  const handleSend = () => {
    if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  };
  // Handle saving insight to Overview
  const handleSaveToOverview = (content: string, type: 'general' | 'trade-idea' = 'general') => {
    const newInsight = {
      id: `briefing-${Date.now()}`,
      content: stripMarkdown(content).slice(0, 200),
      fullContent: content,
      timestamp: new Date().toISOString(),
      type,
      source: 'briefing'
    };
    // Get existing insights
    const existing = localStorage.getItem('oracle-overview-insights');
    const insights = existing ? JSON.parse(existing) : [];
    // Add new insight
    insights.unshift(newInsight);
    localStorage.setItem('oracle-overview-insights', JSON.stringify(insights));
    // Dispatch event for Overview to refresh
    window.dispatchEvent(new Event('oracle-insights-updated'));
    // Show toast
    setSaveToastMessage('Insight saved to Overview');
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 2000);
  };
  const quickActions = ['Trading opportunities?', 'Explain top risk', 'Price forecast', 'Model comparison', 'Position sizing?'];
  if (isCollapsed) {
    return <div className="fixed top-[64px] right-0 w-12 h-[calc(100vh-64px)] border-l border-slate-200 bg-white flex flex-col items-center py-4 z-30" data-id="element-1007">
        <button onClick={toggleCollapse} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-slate-900" title="Expand Chat" data-id="element-1008">
          <ChevronLeft size={20} data-id="element-1009" />
        </button>
        <div className="mt-8 [writing-mode:vertical-rl] rotate-180 text-sm font-bold text-slate-400 tracking-widest uppercase flex items-center gap-2" data-id="element-1010">
          <Sparkles size={14} className="rotate-90" data-id="element-1011" />
          Ask Oracle
        </div>
      </div>;
  }
  return <div className="fixed top-[64px] right-0 w-[420px] h-[calc(100vh-64px)] border-l border-slate-200 bg-white flex flex-col shadow-xl shadow-slate-200/50 z-30" data-id="element-1012">
      {/* Save Toast */}
      <AnimatePresence data-id="element-1013">
        {showSaveToast && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="absolute top-16 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2" data-id="element-1014">
            <Check size={14} className="text-emerald-400" data-id="element-1015" />
            <span className="text-sm font-medium" data-id="element-1016">{saveToastMessage}</span>
          </motion.div>}
      </AnimatePresence>

      {/* Header */}
      <div className="h-14 border-b border-slate-100 flex items-center justify-between px-4 bg-white/80 backdrop-blur-sm" data-id="element-1017">
        <div className="flex items-center gap-2" data-id="element-1018">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-pink-500 to-purple-600 flex items-center justify-center text-white shadow-md shadow-pink-500/20" data-id="element-1019">
            <Sparkles size={16} data-id="element-1020" />
          </div>
          <div data-id="element-1021">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-1022">Ask Oracle</h3>
            <p className="text-[10px] text-slate-500 font-medium" data-id="element-1023">
              AI Trading Assistant
            </p>
          </div>
        </div>
        <button onClick={toggleCollapse} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600" data-id="element-1024">
          <ChevronRight size={18} data-id="element-1025" />
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/30" data-id="element-1026">
        {messages.length === 0 && <div className="text-center py-12 px-6" data-id="element-1027">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-1028">
              <MessageSquare size={20} className="text-slate-400" data-id="element-1029" />
            </div>
            <p className="text-sm font-medium text-slate-900 mb-1" data-id="element-1030">
              No messages yet
            </p>
            <p className="text-xs text-slate-500 leading-relaxed" data-id="element-1031">
              Ask about market trends, specific price forecasts, or risk
              analysis based on the current briefing.
            </p>
          </div>}

        {messages.map((msg, idx) => <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1032">
            <div className={`max-w-[85%] rounded-2xl text-sm leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white rounded-br-sm p-3.5' : 'bg-white text-slate-700 rounded-bl-sm border border-slate-200 p-4'}`} data-id="element-1033">
              {msg.role === 'user' ? <div className="whitespace-pre-wrap font-medium" data-id="element-1034">
                  {msg.content}
                </div> : <>
                  <div className="space-y-1" data-id="element-1035">
                    {renderChatMarkdown(msg.content)}
                  </div>
                  {/* Action buttons for assistant messages */}
                  <div className="flex items-center gap-1 mt-3 pt-2 border-t border-slate-100" data-id="element-1036">
                    <button onClick={() => handleSaveToOverview(msg.content, 'general')} className="p-1.5 rounded-lg hover:bg-pink-50 text-slate-400 hover:text-pink-600 transition-colors group relative" title="Save to Overview" data-id="element-1037">
                      <Bookmark size={12} data-id="element-1038" />
                    </button>
                    <button onClick={() => handleSaveToOverview(msg.content, 'trade-idea')} className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors" title="Save as Trade Idea" data-id="element-1039">
                      <TrendingUp size={12} data-id="element-1040" />
                    </button>
                  </div>
                </>}
              <div className={`text-[10px] mt-1.5 opacity-50 font-mono ${msg.role === 'user' ? 'text-right' : 'text-left'}`} data-id="element-1041">
                {new Date(msg.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
              </div>
            </div>
          </div>)}

        {isLoading && <div className="flex justify-start" data-id="element-1042">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-none p-4 shadow-sm flex items-center gap-3" data-id="element-1043">
              <Loader2 size={16} className="text-pink-500 animate-spin" data-id="element-1044" />
              <span className="text-xs font-medium text-slate-500" data-id="element-1045">
                Analyzing market data...
              </span>
            </div>
          </div>}

        {/* Follow-up suggestions */}
        {!isLoading && messages.length > 0 && suggestedFollowUps.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-wrap gap-2 mt-2" data-id="element-1046">
            {suggestedFollowUps.slice(0, 3).map((suggestion, idx) => <button key={idx} onClick={() => sendMessage(stripMarkdown(suggestion))} className="flex items-center gap-1 px-2.5 py-1.5 bg-white border border-pink-200 hover:border-pink-400 text-pink-700 text-[11px] font-medium rounded-full transition-all hover:shadow-sm" data-id="element-1047">
                <span className="line-clamp-1" data-id="element-1048">
                  {stripMarkdown(suggestion)}
                </span>
                <ArrowRight size={10} data-id="element-1049" />
              </button>)}
          </motion.div>}

        <div ref={messagesEndRef} data-id="element-1050" />
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-slate-100" data-id="element-1051">
        {/* Quick Actions */}
        <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide mb-1" data-id="element-1052">
          {quickActions.map(action => <button key={action} onClick={() => sendMessage(action)} className="whitespace-nowrap px-3 py-1.5 bg-slate-50 hover:bg-pink-50 border border-slate-200 hover:border-pink-200 rounded-full text-xs font-medium text-slate-600 hover:text-pink-700 transition-colors flex-shrink-0" data-id="element-1053">
              {action}
            </button>)}
        </div>

        <div className="relative" data-id="element-1054">
          <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about market impact..." className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all resize-none h-[52px] scrollbar-hide" data-id="element-1055" />
          <button onClick={handleSend} disabled={!inputValue.trim() || isLoading} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:hover:bg-pink-500 transition-colors shadow-sm shadow-pink-500/20" data-id="element-1056">
            <Send size={14} data-id="element-1057" />
          </button>
        </div>
        <div className="text-[10px] text-slate-400 mt-2 text-center font-medium" data-id="element-1058">
          Press <kbd className="font-sans" data-id="element-1059">⌘</kbd> +{' '}
          <kbd className="font-sans" data-id="element-1060">Enter</kbd> to send
        </div>
      </div>
    </div>;
};
// Icon mapping helper with consistent semantic colors per driver type
const DRIVER_ICON_STYLES: Record<string, {
  bg: string;
  text: string;
}> = {
  wind: {
    bg: 'bg-sky-50',
    text: 'text-sky-600'
  },
  sun: {
    bg: 'bg-amber-50',
    text: 'text-amber-600'
  },
  gauge: {
    bg: 'bg-slate-100',
    text: 'text-slate-600'
  },
  activity: {
    bg: 'bg-purple-50',
    text: 'text-purple-600'
  },
  arrows: {
    bg: 'bg-blue-50',
    text: 'text-blue-600'
  },
  chart: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-600'
  }
};
const getDriverIcon = (iconName: string) => {
  const style = DRIVER_ICON_STYLES[iconName] || {
    bg: 'bg-slate-50',
    text: 'text-slate-600'
  };
  const icons: Record<string, React.ReactNode> = {
    wind: <Wind size={20} className={style.text} data-id="element-1061" />,
    sun: <Sun size={20} className={style.text} data-id="element-1062" />,
    gauge: <Gauge size={20} className={style.text} data-id="element-1063" />,
    activity: <Activity size={20} className={style.text} data-id="element-1064" />,
    arrows: <ArrowLeftRight size={20} className={style.text} data-id="element-1065" />,
    chart: <BarChart3 size={20} className={style.text} data-id="element-1066" />
  };
  return icons[iconName] || <Zap size={20} className="text-slate-600" data-id="element-1067" />;
};
const getDriverIconBackground = (iconName: string) => {
  return DRIVER_ICON_STYLES[iconName]?.bg || 'bg-slate-50';
};
// --- Briefing Components ---
const ContextHeader = ({
  region,
  horizonConfig,
  regionData,
  onOpenPreferences
}: {
  region: string;
  horizonConfig: (typeof HORIZON_CONFIG)[DecisionHorizon];
  regionData: (typeof REGION_DATA)[Region];
  onOpenPreferences: () => void;
}) => {
  return <div className="flex items-center justify-between py-4 border-b border-slate-200 mb-6 gap-4" data-id="element-1068">
      <div className="flex-1 min-w-0" data-id="element-1069">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3 flex-wrap" data-id="element-1070">
          {region} Briefing
          <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200" data-id="element-1071">
            {regionData.market}
          </span>
        </h1>
        <p className="text-slate-500 text-sm mt-1" data-id="element-1072">
          <span className="font-semibold text-slate-700" data-id="element-1073">
            {horizonConfig.delivery}
          </span>
          {' • '}
          {horizonConfig.timeframe}
          {' • '}
          {new Date().toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })}
        </p>
      </div>
      <div className="flex items-center gap-4 shrink-0" data-id="element-1074">
        <div className="text-right" data-id="element-1075">
          <div className="text-3xl font-mono font-bold text-slate-900" data-id="element-1076">
            {new Date().toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
          })}
          </div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1077">
            {regionData.timezone}
          </div>
        </div>
        <button onClick={onOpenPreferences} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" title="Edit Briefing Preferences" data-id="element-1078">
          <SlidersHorizontal size={18} data-id="element-1079" />
        </button>
      </div>
    </div>;
};
const ExecutiveSummary = ({
  text
}: {
  text: string;
}) => <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-lg mb-6 flex items-start gap-4" data-id="element-1080">
    <div className="p-2 bg-white/10 rounded-lg shrink-0" data-id="element-1081">
      <Zap size={24} className="text-yellow-400" data-id="element-1082" />
    </div>
    <div data-id="element-1083">
      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1084">
        Executive Summary
      </h3>
      <p className="text-lg font-medium leading-relaxed" data-id="element-1085">{text}</p>
    </div>
  </div>;
const PriceContext = ({
  priceData,
  currency,
  detailLevel
}: {
  priceData: ReturnType<typeof generateBriefingData>['priceData'];
  currency: string;
  detailLevel: DetailLevel;
}) => {
  const isPositive = priceData.main.delta > 0;
  return <div className="mb-8" data-id="element-1086">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-1087">
        Price Context
      </h3>
      <div className={`grid gap-4 ${priceData.tertiary ? 'grid-cols-1 md:grid-cols-3' : priceData.secondary ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`} data-id="element-1088">
        <div className="bg-white/60 backdrop-blur-xl border border-slate-50 p-6 rounded-2xl shadow-sm shadow-slate-200/50 flex items-center justify-between relative overflow-hidden group" data-id="element-1089">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" data-id="element-1090" />
          <div className="relative" data-id="element-1091">
            <p className="text-sm text-slate-500 font-medium" data-id="element-1092">
              {priceData.main.label}
            </p>
            <div className="text-3xl font-bold text-slate-900 mt-1" data-id="element-1093">
              {currency}
              {typeof priceData.main.value === 'number' ? priceData.main.value.toFixed(2) : priceData.main.value}
            </div>
          </div>
          <div className={`text-right relative ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-1094">
            <div className="flex items-center justify-end gap-1 font-bold text-lg" data-id="element-1095">
              {isPositive ? <TrendingUp size={20} data-id="element-1096" /> : <TrendingDown size={20} data-id="element-1097" />}
              {isPositive ? '+' : ''}
              {priceData.main.delta.toFixed(2)}
            </div>
            <p className="text-xs font-medium opacity-80" data-id="element-1098">vs yesterday</p>
          </div>
        </div>

        {priceData.secondary && <div className={`backdrop-blur-xl p-6 rounded-2xl shadow-sm shadow-slate-200/50 flex items-center justify-between relative overflow-hidden group ${priceData.secondary.highlight ? 'bg-blue-50/60 border border-blue-100' : 'bg-white/60 border border-slate-50'}`} data-id="element-1099">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-blue-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" data-id="element-1100" />
            <div className="relative" data-id="element-1101">
              <p className={`text-sm font-medium ${priceData.secondary.highlight ? 'text-blue-700' : 'text-slate-500'}`} data-id="element-1102">
                {priceData.secondary.label}
              </p>
              <div className={`text-3xl font-bold mt-1 ${priceData.secondary.highlight ? 'text-blue-900' : 'text-slate-900'}`} data-id="element-1103">
                {typeof priceData.secondary.value === 'number' ? `${currency}${priceData.secondary.value.toFixed(2)}` : priceData.secondary.value}
              </div>
            </div>
            {priceData.secondary.delta !== 0 && <div className="text-right relative" data-id="element-1104">
                <div className="font-bold text-lg text-slate-700" data-id="element-1105">
                  {priceData.secondary.delta > 0 ? '+' : ''}
                  {priceData.secondary.delta.toFixed(2)}
                </div>
              </div>}
          </div>}

        {priceData.tertiary && <div className="bg-slate-50/60 backdrop-blur-xl border border-slate-100 p-6 rounded-2xl shadow-sm shadow-slate-200/50 relative overflow-hidden group" data-id="element-1106">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-100/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" data-id="element-1107" />
            <div className="relative" data-id="element-1108">
              <p className="text-sm font-medium text-slate-500" data-id="element-1109">
                {priceData.tertiary.label}
              </p>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-id="element-1110">
                {priceData.tertiary.value}
              </div>
              <p className="text-xs text-slate-400 mt-1" data-id="element-1111">
                {priceData.tertiary.context}
              </p>
            </div>
          </div>}
      </div>
    </div>;
};
const DriverInsights = ({
  insights,
  detailLevel
}: {
  insights: ReturnType<typeof generateBriefingData>['driverInsights'];
  detailLevel: DetailLevel;
}) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  if (insights.length === 0) {
    return <div className="mb-8" data-id="element-1112">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-1113">
          Key Drivers
        </h3>
        <div className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-2xl p-6 text-center shadow-sm shadow-slate-200/50" data-id="element-1114">
          <Info size={24} className="text-slate-400 mx-auto mb-2" data-id="element-1115" />
          <p className="text-slate-600" data-id="element-1116">
            Select trading drivers in preferences to see relevant insights.
          </p>
        </div>
      </div>;
  }
  return <div className="mb-8" data-id="element-1117">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-1118">
        Key Drivers
      </h3>
      <div className="space-y-3" data-id="element-1119">
        {insights.map((insight, idx) => <motion.div key={insight.driver} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-2xl overflow-hidden shadow-sm shadow-slate-200/50 relative group" data-id="element-1120">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none" data-id="element-1121" />
            <div className={`p-5 relative ${detailLevel === 'Analytical' ? 'cursor-pointer' : ''}`} onClick={() => detailLevel === 'Analytical' && setExpandedIdx(expandedIdx === idx ? null : idx)} data-id="element-1122">
              <div className="flex items-start gap-4" data-id="element-1123">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${getDriverIconBackground(insight.icon)}`} data-id="element-1124">
                  {getDriverIcon(insight.icon)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-1125">
                  <div className="flex items-center justify-between mb-1" data-id="element-1126">
                    <h4 className="text-lg font-bold text-slate-900" data-id="element-1127">
                      {insight.driver}
                    </h4>
                    <div className="flex items-center gap-2" data-id="element-1128">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${insight.trend === 'up' ? 'bg-emerald-100 text-emerald-700' : insight.trend === 'down' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-1129">
                        {insight.delta}
                      </span>
                      {detailLevel === 'Analytical' && <motion.div animate={{
                    rotate: expandedIdx === idx ? 180 : 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-1130">
                          <ChevronDown size={16} className="text-slate-400" data-id="element-1131" />
                        </motion.div>}
                    </div>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-1132">
                    {insight.headline}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm" data-id="element-1133">
                    <span className="text-slate-600" data-id="element-1134">{insight.impact}</span>
                    <span className="text-slate-400" data-id="element-1135">•</span>
                    <span className="text-slate-500" data-id="element-1136">{insight.confidence}</span>
                  </div>
                  {insight.details && detailLevel !== 'Executive' && <p className="text-sm text-slate-500 mt-3 pt-3 border-t border-slate-100" data-id="element-1137">
                      {insight.details}
                    </p>}
                </div>
              </div>
            </div>

            {/* Analytical expanded section */}
            <AnimatePresence data-id="element-1138">
              {detailLevel === 'Analytical' && expandedIdx === idx && insight.modelAgreement && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-slate-100 bg-slate-50/50 px-5 py-4 relative" data-id="element-1139">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1140">
                      Model Comparison
                    </p>
                    <p className="text-sm font-mono text-slate-700" data-id="element-1141">
                      {insight.modelAgreement}
                    </p>
                  </motion.div>}
            </AnimatePresence>
          </motion.div>)}
      </div>
    </div>;
};
const ReassessmentTriggers = ({
  triggers
}: {
  triggers: ReturnType<typeof generateBriefingData>['triggers'];
}) => {
  if (triggers.length === 0) return null;
  return <div className="mb-8" data-id="element-1142">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-1143">
        <Bell size={14} data-id="element-1144" />
        Reassessment Triggers
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1145">
        {triggers.map((trigger, idx) => <motion.div key={trigger.trigger} initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: idx * 0.05
      }} className={`p-4 rounded-2xl backdrop-blur-xl shadow-sm shadow-slate-200/50 relative overflow-hidden group ${trigger.status === 'active' ? 'bg-rose-50/60 border border-rose-100' : trigger.status === 'watch' ? 'bg-amber-50/60 border border-amber-100' : 'bg-emerald-50/60 border border-emerald-100'}`} data-id="element-1146">
            <div className={`absolute inset-0 bg-gradient-to-br from-transparent via-transparent ${trigger.status === 'active' ? 'to-rose-100/30' : trigger.status === 'watch' ? 'to-amber-100/30' : 'to-emerald-100/30'} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none`} data-id="element-1147" />
            <div className="relative" data-id="element-1148">
              <div className="flex items-center justify-between mb-2" data-id="element-1149">
                <span className="font-semibold text-slate-900 text-sm" data-id="element-1150">
                  {trigger.trigger}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${trigger.status === 'active' ? 'bg-rose-200 text-rose-800' : trigger.status === 'watch' ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'}`} data-id="element-1151">
                  {trigger.status}
                </span>
              </div>
              <p className="text-xs text-slate-600 mb-2" data-id="element-1152">
                {trigger.description}
              </p>
              <div className="flex items-center justify-between text-xs" data-id="element-1153">
                <span className="text-slate-500" data-id="element-1154">
                  Threshold: {trigger.threshold}
                </span>
                <span className="font-semibold text-slate-700" data-id="element-1155">
                  Current: {trigger.current}
                </span>
              </div>
            </div>
          </motion.div>)}
      </div>
    </div>;
};
const RiskFlags = ({
  risks,
  detailLevel
}: {
  risks: ReturnType<typeof generateBriefingData>['riskFlags'];
  detailLevel: DetailLevel;
}) => {
  if (risks.length === 0) return null;
  const severityConfig = {
    high: {
      bg: 'bg-rose-50/60',
      border: 'border-rose-100',
      icon: 'text-rose-600',
      title: 'text-rose-900',
      text: 'text-rose-800',
      hover: 'to-rose-100/30'
    },
    medium: {
      bg: 'bg-amber-50/60',
      border: 'border-amber-100',
      icon: 'text-amber-600',
      title: 'text-amber-900',
      text: 'text-amber-800',
      hover: 'to-amber-100/30'
    },
    low: {
      bg: 'bg-blue-50/60',
      border: 'border-blue-100',
      icon: 'text-blue-600',
      title: 'text-blue-900',
      text: 'text-blue-800',
      hover: 'to-blue-100/30'
    }
  };
  return <div className="mb-8" data-id="element-1156">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-1157">
        Risk Flags
      </h3>
      <div className="space-y-3" data-id="element-1158">
        {risks.map((risk, idx) => {
        const config = severityConfig[risk.severity];
        return <motion.div key={risk.title} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: idx * 0.1
        }} className={`${config.bg} backdrop-blur-xl border ${config.border} rounded-2xl p-5 shadow-sm shadow-slate-200/50 relative overflow-hidden group`} data-id="element-1159">
              <div className={`absolute inset-0 bg-gradient-to-br from-transparent via-transparent ${config.hover} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none`} data-id="element-1160" />
              <div className="flex gap-3 relative" data-id="element-1161">
                <AlertTriangle className={`${config.icon} shrink-0`} size={20} data-id="element-1162" />
                <div className="flex-1" data-id="element-1163">
                  <div className="flex items-center justify-between mb-1" data-id="element-1164">
                    <h4 className={`font-bold ${config.title}`} data-id="element-1165">
                      {risk.title}
                    </h4>
                    {detailLevel !== 'Executive' && <span className="text-xs font-medium text-slate-500" data-id="element-1166">
                        {risk.probability}% probability
                      </span>}
                  </div>
                  <p className={`text-sm ${config.text} leading-relaxed`} data-id="element-1167">
                    {risk.description}
                  </p>
                  {detailLevel !== 'Executive' && <p className={`text-xs ${config.text} mt-2 font-semibold`} data-id="element-1168">
                      Impact: {risk.impact}
                    </p>}
                </div>
              </div>
            </motion.div>;
      })}
      </div>
    </div>;
};
const OperationalEvents = ({
  events,
  detailLevel
}: {
  events: ReturnType<typeof generateBriefingData>['operationalEvents'];
  detailLevel: DetailLevel;
}) => {
  if (events.length === 0) {
    if (detailLevel === 'Executive') return null;
    return <div className="mb-8" data-id="element-1169">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-1170">
          Operational Events
        </h3>
        <div className="bg-emerald-50/60 backdrop-blur-xl border border-emerald-100 rounded-2xl p-4 text-center shadow-sm shadow-slate-200/50" data-id="element-1171">
          <Check size={20} className="text-emerald-600 mx-auto mb-1" data-id="element-1172" />
          <p className="text-sm text-emerald-700 font-medium" data-id="element-1173">
            No significant operational events
          </p>
        </div>
      </div>;
  }
  return <div className="mb-8" data-id="element-1174">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-1175">
        Operational Events
      </h3>
      <div className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-2xl overflow-hidden shadow-sm shadow-slate-200/50" data-id="element-1176">
        <table className="w-full text-sm text-left" data-id="element-1177">
          <thead className="bg-slate-50/50 text-slate-500 font-medium" data-id="element-1178">
            <tr data-id="element-1179">
              <th className="px-4 py-3" data-id="element-1180">Asset</th>
              <th className="px-4 py-3" data-id="element-1181">Type</th>
              <th className="px-4 py-3" data-id="element-1182">Impact</th>
              <th className="px-4 py-3" data-id="element-1183">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100/50" data-id="element-1184">
            {events.map((event, idx) => <motion.tr key={`${event.asset}-${idx}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: idx * 0.05
          }} className="hover:bg-slate-50/30 transition-colors" data-id="element-1185">
                <td className="px-4 py-3 font-medium text-slate-900" data-id="element-1186">
                  {event.asset}
                </td>
                <td className="px-4 py-3 text-slate-500" data-id="element-1187">{event.type}</td>
                <td className={`px-4 py-3 font-medium ${event.impact.startsWith('-') ? 'text-rose-600' : 'text-amber-600'}`} data-id="element-1188">
                  {event.impact}
                </td>
                <td className="px-4 py-3" data-id="element-1189">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${event.status === 'unplanned' ? 'bg-rose-100 text-rose-700' : event.status === 'returning' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-1190">
                    {event.status}
                  </span>
                </td>
              </motion.tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};
const HorizonMetrics = ({
  horizonConfig,
  detailLevel
}: {
  horizonConfig: (typeof HORIZON_CONFIG)[DecisionHorizon];
  detailLevel: DetailLevel;
}) => {
  if (detailLevel === 'Executive') return null;
  return <div className="mb-8" data-id="element-1191">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-1192">
        <Clock size={14} data-id="element-1193" />
        {horizonConfig.timeframe} Focus
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1194">
        {horizonConfig.keyMetrics.map((metric, idx) => <div key={metric} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-2xl p-4 shadow-sm shadow-slate-200/50 relative overflow-hidden group" data-id="element-1195">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none" data-id="element-1196" />
            <div className="relative" data-id="element-1197">
              <p className="text-xs text-slate-500 font-medium mb-1" data-id="element-1198">
                {metric}
              </p>
              <p className="text-lg font-bold text-slate-900" data-id="element-1199">
                {/* Generate varied mock values */}
                {idx === 0 ? '€82.4' : idx === 1 ? '+12.3%' : idx === 2 ? '0.94' : 'Normal'}
              </p>
            </div>
          </div>)}
      </div>
      {detailLevel === 'Analytical' && <div className="mt-3 p-3 bg-slate-50/60 backdrop-blur-xl rounded-xl border border-slate-100" data-id="element-1200">
          <p className="text-xs text-slate-500" data-id="element-1201">
            <span className="font-semibold" data-id="element-1202">Focus areas:</span>{' '}
            {horizonConfig.focusAreas.join(' • ')}
          </p>
          <p className="text-xs text-slate-400 mt-1" data-id="element-1203">
            Updates: {horizonConfig.updateFrequency}
          </p>
        </div>}
    </div>;
};
// --- Main View ---
export function BriefingView() {
  const [preferences, setPreferences] = useState<BriefingPreferences | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  // Load preferences
  useEffect(() => {
    const saved = localStorage.getItem('jua-briefing-preferences');
    if (saved) {
      setPreferences(JSON.parse(saved));
    } else {
      setShowOnboarding(true);
    }
  }, []);
  const handleSavePreferences = (newPrefs: BriefingPreferences) => {
    setPreferences(newPrefs);
    localStorage.setItem('jua-briefing-preferences', JSON.stringify(newPrefs));
    setShowOnboarding(false);
    setShowPreferences(false);
  };
  // Generate briefing data based on preferences
  const briefingData = useMemo(() => {
    if (!preferences) return null;
    return generateBriefingData(preferences);
  }, [preferences]);
  if (!preferences && !showOnboarding) return null;
  return <div className="w-full pb-20 relative" data-id="element-1204">
      {/* Onboarding Modal (Initial Setup) */}
      <OnboardingModal isOpen={showOnboarding} onComplete={handleSavePreferences} data-id="element-1205" />

      {/* Preferences Panel (Edit Settings) */}
      {preferences && <PreferencesPanel isOpen={showPreferences} onClose={() => setShowPreferences(false)} onSave={handleSavePreferences} initialPrefs={preferences} data-id="element-1206" />}

      {/* Main Content Layout - Now full width since chat is handled by global widget */}
      {preferences && briefingData && <motion.div key={JSON.stringify(preferences)} // Re-animate when preferences change
    initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="w-full" data-id="element-1207">
          <ContextHeader region={briefingData.region} horizonConfig={briefingData.horizonConfig} regionData={briefingData.regionData} onOpenPreferences={() => setShowPreferences(true)} data-id="element-1208" />

          <ExecutiveSummary text={briefingData.executiveSummary} data-id="element-1209" />

          <PriceContext priceData={briefingData.priceData} currency={briefingData.regionData.currency} detailLevel={preferences.detailLevel} data-id="element-1210" />

          <HorizonMetrics horizonConfig={briefingData.horizonConfig} detailLevel={preferences.detailLevel} data-id="element-1211" />

          <DriverInsights insights={briefingData.driverInsights} detailLevel={preferences.detailLevel} data-id="element-1212" />

          <ReassessmentTriggers triggers={briefingData.triggers} data-id="element-1213" />

          <RiskFlags risks={briefingData.riskFlags} detailLevel={preferences.detailLevel} data-id="element-1214" />

          <OperationalEvents events={briefingData.operationalEvents} detailLevel={preferences.detailLevel} data-id="element-1215" />

          {/* Footer Note */}
          <div className="text-center text-xs text-slate-400 mt-8 mb-12 space-y-1" data-id="element-1216">
            <p data-id="element-1217">
              Briefing generated for{' '}
              <span className="font-semibold text-slate-500" data-id="element-1218">
                {preferences.detailLevel}
              </span>{' '}
              profile •{' '}
              <span className="font-semibold text-slate-500" data-id="element-1219">
                {preferences.horizon}
              </span>{' '}
              horizon •{' '}
              <span className="font-semibold text-slate-500" data-id="element-1220">
                {preferences.tradingStyle}
              </span>{' '}
              focus
            </p>
            <p data-id="element-1221">
              Regions: {preferences.regions.join(', ')} • Drivers:{' '}
              {preferences.drivers.length} selected • Triggers:{' '}
              {preferences.reassessmentTriggers.length} active
            </p>
          </div>
        </motion.div>}
    </div>;
}